
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { logVersionInfo, handleVersionMismatch } from "@/utils/versionManager";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Shield, Star, Zap, Heart } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import AdPanel from "@/components/ads/AdPanel";
import MatchHorizontalView from "@/components/matches/MatchHorizontalView";
import { AlphaLoader } from "@/components/ui/alpha-loader";

interface Match {
  user_id: string;
  display_name: string | null;
  photo_urls: string[] | null;
  common_count: number;
  match_tier: string;
  matched_at?: string;
  bio?: string;
}

const Matches = () => {
  const { user } = useAuth();
  const [matches, setMatches] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Enhanced version logging and cache management
    const deviceInfo = logVersionInfo('Matches', { userId: user?.id });
    
    // Handle version mismatches immediately for Samsung devices
    if (deviceInfo.isSamsung) {
      handleVersionMismatch();
    }
    
    // Service Worker enhanced version checking
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_VERSION_UPDATE' || event.data?.type === 'SW_VERSION_CHECK') {
          console.log('🔄 SW Version Message:', event.data);
          
          // Immediate action for Samsung devices
          if (deviceInfo.isSamsung && event.data.version !== deviceInfo.version) {
            console.log('⚠️ Samsung device version mismatch, forcing immediate reload...');
            setTimeout(() => window.location.reload(), 500);
          }
        }
      });
    }
    
    if (user) {
      fetchMatches();
    }
  }, [user]);

  const fetchMatches = async () => {
    if (!user) return;

    try {
      // Get matches from profile_compatibility view
      const { data: compatibilityData } = await supabase
        .from('profile_compatibility')
        .select('user2_id, common_count, match_tier')
        .eq('user1_id', user.id)
        .order('common_count', { ascending: false });

      if (!compatibilityData || compatibilityData.length === 0) {
        setMatches([]);
        return;
      }

      // Get profile data for matches
      const matchUserIds = compatibilityData.map(match => match.user2_id);
      
      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, photo_urls, bio')
        .in('user_id', matchUserIds);

      // Combine compatibility data with profile data
      const formattedMatches = compatibilityData.map(match => {
        const profile = profiles?.find(p => p.user_id === match.user2_id);
        return {
          user_id: match.user2_id,
          display_name: profile?.display_name || null,
          photo_urls: profile?.photo_urls || null,
          bio: profile?.bio || null,
          common_count: match.common_count,
          match_tier: match.match_tier,
          matched_at: new Date().toISOString() // Placeholder since we don't have actual match dates
        };
      });

      setMatches(formattedMatches);
    } catch (error: any) {
      console.error('Error fetching matches:', error);
      toast({
        title: "Error",
        description: "Failed to load matches",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (matchId: string) => {
    console.log('Approved match:', matchId);
    // TODO: Store approval in database
    toast({
      title: "Match Approved",
      description: "You can now message this person!",
    });
  };

  const handleReject = async (matchId: string) => {
    console.log('Rejected match:', matchId);
    // TODO: Store rejection in database
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <RightSideMenu />
        <AlphaLoader size="small" />
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-background p-4 pb-20">
      <RightSideMenu />
      <AdPanel />
      
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold neon-text mb-4">
            <Users className="inline-block w-10 h-10 text-neon-red mr-3" />
            Compatibility Matches
          </h1>
          <p className="text-ice-white/80 text-lg">Scroll through your perfect matches</p>
        </div>

        {matches.length === 0 ? (
          <div className="dark-pod p-8 text-center">
            <Shield className="w-16 h-16 text-ice-white/40 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-ice-white mb-4">No Matches Yet</h2>
            <p className="text-ice-white/60 mb-6 leading-relaxed">
              Add more private hashtags to your profile to find people who share your interests!
            </p>
            <div className="bg-ice-white/10 p-4 rounded-lg mb-6 border border-ice-white/20">
              <p className="text-sm text-ice-white/80">
                <Shield className="w-4 h-4 inline mr-2 text-electric-violet" />
                Your private hashtags are used for matching but never shown to others until you start messaging.
              </p>
            </div>
            <Link
              to="/complete-profile"
              className="bdsm-button inline-flex items-center justify-center px-6 py-3 text-lg font-semibold"
            >
              Update Profile
            </Link>
          </div>
        ) : (
          <MatchHorizontalView
            matches={matches}
            isLoading={loading}
            onRefresh={fetchMatches}
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}
      </div>
    </div>
  );
};

export default Matches;
